import React, { useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { useForm } from '../../hooks/useForm';
import { useFetchUsers, useDeleteUser } from '../../services/users';
import { useUser } from '../../contexts/UserContext';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import DataTable from '../common/DataTable';
import Pagination from '../common/Pagination';

import ProviderModal from './modals/ProviderModal';

const Providers = () => {
  const { hasPermission } = useUser();
  const { form, onChange } = useForm({ search: '' });
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'userLastName',
    sortDir: 'ASC',
  });
  const [userModal, setUserModal] = useState(false);
  const queryClient = useQueryClient();

  const { isLoading, data } = useFetchUsers(
    filters.page,
    filters.search,
    filters.sortBy,
    filters.sortDir,
    'provider',
  );

  const deleteUser = useDeleteUser(
    () => {
      toast.success('The provider has been successfully removed.');
      queryClient.invalidateQueries('provider');
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: form.search });
  };

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>Providers</h2>
              </Col>
              {hasPermission('providers:manage') && (
                <Col xs={6} sm={3} className="text-end">
                  <Button
                    variant="outline-primary"
                    onClick={() => setUserModal({ _claims: [] })}
                  >
                    <FontAwesomeIcon icon={faUser} /> Create Provider
                  </Button>
                </Col>
              )}
              <Col xs={12} sm={4} className="text-end">
                <form onSubmit={onSubmit}>
                  <Form.Control
                    name="search"
                    value={form.search}
                    type="search"
                    placeholder="Search..."
                    onChange={onChange}
                  ></Form.Control>
                </form>
              </Col>
            </Row>
            <br />
            <DataTable
              columns={[
                {
                  header: 'Last Name',
                  key: 'userLastName',
                  sortable: true,
                },
                {
                  header: 'First Name',
                  key: 'userFirstName',
                  sortable: true,
                },
                {
                  header: 'Email',
                  key: 'userEmail',
                  sortable: true,
                },
                {
                  header: 'Access To',
                  key: '_claims',
                  sortable: true,
                  render: (row) => {
                    return row._claims ? row._claims.length : 0;
                  },
                },
              ]}
              busy={isLoading}
              actions={hasPermission('providers:manage') ? [
                {
                  label: 'Edit Provider',
                  onClick: (row) => setUserModal(row),
                },
                {
                  divider: true,
                },
                {
                  label: 'Delete Provider',
                  onClick: (row) => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete/deactivate this user?',
                      )
                    ) {
                      deleteUser.mutate(row.userId);
                    }
                  },
                },
              ] : undefined}
              data={data ? data.rows : []}
              defaultSort={`${filters.sortBy}:${filters.sortDir}`}
              onSortChange={(newSortBy, newSortDir) => {
                setFilters({
                  ...filters,
                  page: 1,
                  sortBy: newSortBy,
                  sortDir: newSortDir,
                });
              }}
            />

            {data && data.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={data.total}
                rowsPerPage={50}
                onBack={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                onForward={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
              />
            ) : null}
          </Panel>
        </Col>
      </Row>
      <ProviderModal
        show={userModal !== false}
        toggle={() => {
          setUserModal(false);
        }}
        onSuccess={() => queryClient.invalidateQueries('provider')}
        user={userModal}
      />
    </PrivateLayout>
  );
};

export default Providers;
